// Pairing service — polling de status do código QR (Fase 4A.7-C).
//
// Estratégia:
// - Init gera código + URL. App mostra QR.
// - Polling a cada 3s até status terminal (approved/denied/expired).
// - AbortController pra cancelar quando user sair da tela.
// - Quando approved, retorna tokens swipeuser pra o caller chamar
//   /preference/resolve + api.login(Xtream).
//
// Rate limit do backend: POLL_LIMIT 60/min = 1 req/s. 3s entre polls
// é folga grande, sem risco.

import { api } from '../api'

const POLL_INTERVAL_MS = 3000
/** Readiness pós-aprovação (P2): a conta pode demorar pra salvar a fonte
 *  enquanto o usuário mexe no celular/site. Intervalo curto (2.5s) pra a TV
 *  reagir rápido, com TTL generoso (10min) — janela típica de onboarding. */
const SETUP_POLL_INTERVAL_MS = 2500
const SETUP_POLL_TIMEOUT_MS = 10 * 60 * 1000

export type PollResult =
  | { status: 'approved'; accessToken: string; refreshToken: string }
  | { status: 'denied' }
  | { status: 'expired' }

export type SetupPollResult = { hasSource: boolean; profileCount: number } | { status: 'timeout' }

/**
 * Polla o status do código até terminal. Retorna o resultado final.
 * Cancela cedo se `signal` abortar (lança `AbortError`).
 */
export async function pollPairingStatus(code: string, signal: AbortSignal): Promise<PollResult> {
  while (!signal.aborted) {
    const result = await api.swipePairingStatus(code)
    if (result.status === 'approved') {
      return {
        status: 'approved',
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      }
    }
    if (result.status === 'denied') return { status: 'denied' }
    if (result.status === 'expired') return { status: 'expired' }
    // status === 'pending' → continua
    await sleepCancellable(POLL_INTERVAL_MS, signal)
  }
  throw new DOMException('aborted', 'AbortError')
}

/**
 * Polla o readiness da conta (setup-status) até a conta TER FONTE
 * (`hasSource === true`) ou estourar o TTL. "Pronto pra TV" = ter fonte; o
 * perfil resolve depois na própria TV (tela /profiles). Best-effort: erro de
 * rede NÃO derruba o poll — continua tentando dentro do TTL (o usuário pode
 * estar trocando de rede no celular). Cancela cedo se `signal` abortar
 * (lança `AbortError`); o caller faz cleanup do AbortController no unmount.
 */
export async function pollAccountSetup(
  swipeAccessToken: string,
  signal: AbortSignal,
): Promise<SetupPollResult> {
  const deadline = Date.now() + SETUP_POLL_TIMEOUT_MS
  while (!signal.aborted) {
    try {
      const status = await api.swipeSetupStatus(swipeAccessToken)
      if (status.hasSource) return status
    } catch {
      // Best-effort: rede instável durante o setup não encerra a espera.
      // Segue pro sleep e tenta de novo até o TTL.
    }
    if (Date.now() >= deadline) return { status: 'timeout' }
    await sleepCancellable(SETUP_POLL_INTERVAL_MS, signal)
  }
  throw new DOMException('aborted', 'AbortError')
}

/** Sleep que respeita AbortSignal — rejeita com AbortError se cancelado. */
function sleepCancellable(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('aborted', 'AbortError'))
      return
    }
    const t = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(t)
      reject(new DOMException('aborted', 'AbortError'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}
